import { cn } from "@/lib/utils";

import { CHIP_FILLED, CHIP_ROOT } from "./chip-shape";

// A wrapping row of removable chips, drawn the way the ported screens' chips
// are drawn.
//
// The look is MUI's default filled `Chip` — the donor's shared `ChipList`
// (`timmy/src/components/chip-list`) renders one per entry, and the theme adds no
// `MuiChip` override, so MUI's defaults are the source spec. The box itself is
// `CHIP_ROOT` + `CHIP_FILLED` from `./chip-shape`, which is where those numbers
// are stated and cited; what is particular to *this* row is:
//
//   - 12px of padding, dropping to 5px on the side the delete icon is on —
//     MUI's icon margin, `0 5px 0 -6px` against the label's 12px, so the icon
//     sits 6px off the words and 5px in from the edge (#753, measured beside
//     the donor's Homesites chips),
//   - the chip itself the tab stop, Backspace or Delete removing it, its
//     keyboard focus a ground tint rather than a ring — MUI's ButtonBase
//     semantics (`removableChip` below),
//   - a 22px delete icon, tinted `primary.main` (#003c77) — the donor's one
//     override of MUI's grey default, spelled in its `sx` — and it is MUI's
//     `CancelIcon`: a *filled* disc with the cross knocked out of it, not an
//     outline cross. The tint is unconditional, hover included, because the
//     donor's `sx` override outranks MUI's own hover rule.
//
// A `<ul>` of `<li>`s, which is the donor's markup too: a chip row is a list of
// what is applied, and a screen reader should be able to count it.
//
// **Shared rather than page-local** (`docs/agents/migration-guidelines.md`,
// "Shared donor components port as shared Atlas components"): the item catalog
// sidebar draws applied manufacturer filters with it, and the item search
// island draws tier chips with the same component in the donor.
//
// Distinct from `active-filter-pills.tsx` next door, which is this repo's own
// admin-surface pill row (a different, smaller look with a "Clear all" button)
// and belongs to screens that have no modsy donor.

const isDeleteKey = (event: React.KeyboardEvent) =>
  event.key === "Backspace" || event.key === "Delete";

/**
 * MUI's deletable chip is a ButtonBase (`Chip.js`): the chip itself is the tab
 * stop, a `button` named by its words, and Backspace or Delete removes it —
 * held back on the press, so the browser starts no navigation, and acted on at
 * the release. Enter and Space do nothing: the donor passes no `onClick`.
 */
function removableChip(remove: () => void) {
  return {
    role: "button",
    tabIndex: 0,
    "aria-keyshortcuts": "Backspace Delete",
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.target === event.currentTarget && isDeleteKey(event)) event.preventDefault();
    },
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.target === event.currentTarget && isDeleteKey(event)) remove();
    },
  } as const;
}

export interface ChipListEntry {
  /** Stable across renders — a filter's id, not its index. */
  key: string;
  label: string;
}

interface ChipListProps {
  chips: readonly ChipListEntry[];
  /**
   * Called with the entry's `key`. Omitted, the chips are not removable and no
   * delete icon is drawn — the donor's `onDelete` is optional in the same way.
   */
  onRemove?: (key: string) => void;
  /**
   * How the chips are spaced. `gap` (the default) puts 8px *between* them.
   * `margins` is the donor's own arrangement — each chip carries `mr 8 / mb 8`,
   * the last included (`timmy/src/components/chip-list/chip-list.tsx:33-41`) —
   * so a chip wraps where its trailing 8px would overflow, two lists side by
   * side sit 8px apart, and a row of 32px chips stands 40px. The search
   * island's applied-filter row draws it (#895); the screens ported before it
   * keep the gaps they were measured with.
   */
  spacing?: "gap" | "margins";
  className?: string;
  "data-testid"?: string;
}

export function ChipList({
  chips,
  onRemove,
  spacing = "gap",
  className,
  "data-testid": testId = "chip-list",
}: ChipListProps) {
  if (chips.length === 0) return null;

  return (
    <ul
      className={cn(
        "m-0 flex list-none flex-wrap p-0",
        spacing === "gap" ? "gap-x-2 gap-y-2" : "[&>li]:mr-2 [&>li]:mb-2",
        className,
      )}
      data-testid={testId}
    >
      {chips.map((chip) => (
        <li key={chip.key}>
          <span
            {...(onRemove ? removableChip(() => onRemove(chip.key)) : {})}
            className={cn(
              CHIP_ROOT,
              CHIP_FILLED,
              // On the root rather than on a label span, because this chip's
              // label and its delete icon are siblings — there is no label
              // element to pad. MUI pads its label; the resolved inset is the
              // same.
              "pl-3",
              onRemove
                ? // `.Mui-focusVisible` on a deletable chip: `action.selected`
                  // at 0.08 + `focusOpacity` 0.12. A mouse's focus tints nothing.
                  "focus-visible:bg-lennar-action-selected-focus pr-[5px]"
                : "pr-3",
            )}
          >
            {chip.label}
            {onRemove ? (
              // Material's `cancel` glyph, the shape MUI's `Chip` deletes with —
              // inlined rather than approximated with an outline cross from the
              // icon set, because the filled disc is what the source screen
              // shows. 22px, 6px off the words (the label's 12px less the
              // icon's −6px margin). A mouse target only, `aria-hidden` as
              // MUI's is: the keyboard deletes from the chip itself.
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                data-slot="chip-delete"
                onClick={(event) => {
                  // MUI's `handleDeleteIconClick`: the chip under it never hears it.
                  event.stopPropagation();
                  onRemove(chip.key);
                }}
                className="text-lennar-blue-600 ml-1.5 size-[22px] shrink-0 cursor-pointer fill-current"
              >
                <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
              </svg>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
